/**
 * Ruta legacy `/r/:slugOrId`.
 * Resolver de compatibilidad: traduce el identificador antiguo (menu_slug, slug
 * de restaurante o UUID) al menu_slug canónico y redirige a `/:slug`.
 * La experiencia real vive únicamente en PublicMenuBySlug.
 */

import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UtensilsCrossed } from "lucide-react";

export default function PublicMenu() {
  const { slugOrId } = useParams<{ slugOrId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      if (!slugOrId) {
        setNotFound(true);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('resolve_public_menu_slug', {
          identifier: slugOrId
        });

        if (cancelled) return;

        const resolvedSlug = typeof data === 'string' ? data : null;

        if (error || !resolvedSlug) {
          setNotFound(true);
          return;
        }

        const search = location.search || '';
        navigate(`/${encodeURIComponent(resolvedSlug)}${search}`, { replace: true });
      } catch {
        if (!cancelled) setNotFound(true);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
    // Solo depende del identificador: evita redirecciones repetidas
  }, [slugOrId]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Menú no disponible</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <UtensilsCrossed className="w-16 h-16 mx-auto text-muted-foreground" />
            <h1 className="text-2xl font-bold">Menú no disponible</h1>
            <p className="text-muted-foreground">
              Este enlace ya no está activo. Pide al restaurante su enlace o código QR actual.
            </p>
            <Button onClick={() => navigate('/')} className="w-full min-h-[44px]">
              Ir al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <p className="text-muted-foreground text-sm">Abriendo el menú…</p>
    </div>
  );
}
